// Per-user, client-side feature-flag override stored in localStorage. Lets a
// specific user opt into a flag in production without an org/role feature-set
// change — mirroring the GLaDOS `userFlagOverrides` util and the previous
// agent-desktop `feature_*` localStorage override.
//
// Allowlisted so an arbitrary flag can never be silently flipped from the
// console. Overrides are cleared on logout (see actions/user/log_out) so a
// console toggle on a shared machine can't leak into the next agent's session.
//
// TODO(sc-247885): temporary mechanism for opting specific users into
// `newMainNav`. Remove this module, its `use_is_feature_enabled` wiring, the
// `log_out` clear call, and the allowlist entry once `newMainNav` ships to
// everyone.

const STORAGE_PREFIX = 'feature_';

// Only these flags may be overridden from localStorage.
const OVERRIDABLE_FLAGS = ['newMainNav'];

function storageKey(feature) {
  return `${STORAGE_PREFIX}${feature}`;
}

// Reads localStorage defensively. Accessing `window.localStorage` (or its
// methods) can throw — e.g. `SecurityError` when storage is blocked — and this
// runs during render via `useIsFeatureEnabled`, so a throw must degrade to "no
// override" rather than crash the app.
function readOverride(feature) {
  try {
    return window.localStorage.getItem(storageKey(feature)) === 'true';
  } catch {
    return false;
  }
}

// Writes (or clears) localStorage defensively; a no-op when storage is blocked.
function writeOverride(feature, enabled) {
  try {
    if (enabled) {
      window.localStorage.setItem(storageKey(feature), 'true');
    } else {
      window.localStorage.removeItem(storageKey(feature));
    }
  } catch {
    // Storage blocked/unavailable — nothing to persist.
  }
}

// True when the user has locally enabled an allowlisted flag override.
export function isFeatureOverridden(feature) {
  if (typeof window === 'undefined' || !OVERRIDABLE_FLAGS.includes(feature)) {
    return false;
  }
  return readOverride(feature);
}

// Set or clear an allowlisted flag override.
export function setFeatureOverride(feature, enabled) {
  if (typeof window === 'undefined' || !OVERRIDABLE_FLAGS.includes(feature)) {
    return;
  }
  writeOverride(feature, enabled);
}

// Clears every allowlisted override. Called on logout so a console toggle on a
// shared machine can't leak into the next agent's session.
export function clearFeatureOverrides() {
  if (typeof window === 'undefined') {
    return;
  }
  OVERRIDABLE_FLAGS.forEach(feature => writeOverride(feature, false));
}

// Exposes `window.allowNewMainNav(true|false)` so a targeted user can
// self-enable the new main nav from the browser console.
export function registerFeatureOverrideConsoleHelpers() {
  if (typeof window === 'undefined') {
    return;
  }
  window.allowNewMainNav = enabled => setFeatureOverride('newMainNav', enabled);
}
